import React from "react";
import PreviewContainer from "./PreviewContainer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeBrandColor } from "@/lib/utils";

interface ChatInterfacePreviewProps {
  brandColor: string;
  logoUrl?: string;
  agentName: string;
  organizationName: string;
  showHubfloBranding?: boolean;
  chatBackground?: string;
  organization?: any;
}

const ChatInterfacePreview: React.FC<ChatInterfacePreviewProps> = ({
  brandColor,
  logoUrl,
  agentName,
  organizationName,
  showHubfloBranding = true,
  chatBackground,
  organization,
}) => {
  const safeBrandColor = sanitizeBrandColor(brandColor);

  // Create transparent brand color for gradient (matching real implementation)
  const transparentBrandColor = `${safeBrandColor}26`; // 26 is hex for ~15% opacity

  // Background system matching DesktopChatLayout.tsx
  const containerStyle: React.CSSProperties = {
    background: `linear-gradient(to bottom, ${transparentBrandColor} 0%, #ffffff 100%)`,
    minHeight: "400px",
    position: "relative",
  };

  // Custom background logic matching real implementation
  const hasCustomBackground =
    chatBackground &&
    chatBackground !== "default" &&
    chatBackground !== "Default - Linear Background";

  const customBackgroundUrl = hasCustomBackground
    ? supabase.storage
        .from("image_library")
        .getPublicUrl(`chat_backgrounds/${chatBackground}`).data.publicUrl
    : null;

  return (
    <PreviewContainer title="Live Preview" className="h-fit">
      <div className="w-full mx-auto bg-white border rounded-lg overflow-hidden">
        {/* Chat Header - matching ChatHeader.tsx */}
        <div
          className="text-white px-5 py-4 flex items-center justify-between"
          style={{ backgroundColor: safeBrandColor }}
        >
          <div className="flex items-center gap-4">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={organizationName}
                className="h-10 w-10 object-contain rounded-full bg-white p-1"
              />
            )}
            <div>
              <h2 className="font-semibold text-sm text-white">
                {organizationName}
              </h2>
            </div>
          </div>
        </div>

        {/* Banner Image - matching splash page preview */}
        {(organization as any)?.banner_url && (
          <div className="w-full h-32 overflow-hidden">
            <img
              src={(organization as any).banner_url}
              alt="Organization Banner"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Chat Content Area - matching real layout */}
        <div className="relative overflow-hidden" style={containerStyle}>
          {/* Custom Background Overlay - matching exact implementation */}
          {hasCustomBackground && customBackgroundUrl && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: safeBrandColor,
                maskImage: `url(${customBackgroundUrl})`,
                WebkitMaskImage: `url(${customBackgroundUrl})`,
                maskSize: "cover",
                WebkitMaskSize: "cover",
                maskPosition: "center center",
                WebkitMaskPosition: "center center",
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                opacity: 0.06,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
          )}

          {/* Messages Container - matching max-width from real chat */}
          <div className="max-w-[770px] mx-auto p-6 relative z-10">
            {/* Agent Message - matching ChatMessageBubble.tsx */}
            <div className="mb-6">
              <div className="flex items-start gap-2">
                <div className="relative">
                  <Avatar className="h-[50px] w-[50px]">
                    <AvatarImage
                      src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/avatars/CleanShot%202025-03-29%20at%2016.00.31@2x.png`}
                      alt={agentName}
                      className="object-contain"
                    />
                    <AvatarFallback
                      style={{
                        backgroundColor: safeBrandColor,
                        color: "#ffffff",
                      }}
                    >
                      {agentName.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {/* Green online status dot - matching real implementation */}
                  <div
                    className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
                    style={{ backgroundColor: "#059669" }}
                  />
                </div>
                <div
                  className="bg-white p-3 max-w-[80%] text-black prose prose-sm border-none"
                  style={{
                    borderRadius: "14px",
                    boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <p className="m-0">
                    Hello! I'm here to help you. How can I assist you today?
                  </p>
                </div>
              </div>
            </div>

            {/* User Message - matching exact styling */}
            <div className="flex justify-end mb-6">
              <div
                className="px-4 py-3 max-w-[80%] text-white"
                style={{
                  backgroundColor: safeBrandColor,
                  borderRadius: "14px",
                  boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)",
                }}
              >
                I need help with my account
              </div>
            </div>

            {/* Agent Response */}
            <div className="mb-6">
              <div className="flex items-start gap-2">
                <div className="relative">
                  <Avatar className="h-[50px] w-[50px]">
                    <AvatarImage
                      src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/avatars/CleanShot%202025-03-29%20at%2016.00.31@2x.png`}
                      alt={agentName}
                      className="object-contain"
                    />
                    <AvatarFallback
                      style={{
                        backgroundColor: safeBrandColor,
                        color: "#ffffff",
                      }}
                    >
                      {agentName.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
                    style={{ backgroundColor: "#059669" }}
                  />
                </div>
                <div
                  className="bg-white p-3 max-w-[80%] text-black prose prose-sm border-none"
                  style={{
                    borderRadius: "14px",
                    boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <p className="m-0">
                    I'd be happy to help you with your account! What specific
                    assistance do you need?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Input Area - matching real ChatInput component */}
        <div className="flex items-center gap-3 w-full px-4 py-3 bg-transparent">
          <div
            className="flex-1 relative h-12 rounded-full overflow-hidden"
            style={{
              border: "1px solid #e5e7eb",
              background: "white",
              borderRadius: "9999px",
            }}
          >
            <div
              className="w-full h-full bg-transparent rounded-full flex items-center text-gray-500"
              style={{
                paddingLeft: "18px",
                paddingRight: "60px",
                fontSize: "16px", // Prevent iOS Safari auto-zoom (must be 16px or larger)
              }}
            >
              Type your message...
            </div>

            <div
              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: safeBrandColor,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="m5 12 7-7 7 7M12 5v14"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  transform="rotate(90 12 12)"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Hubflo Branding - matching exact implementation from ChatEngine.tsx */}
        {showHubfloBranding && (
          <div className="relative z-[1] px-4 py-2 text-center bg-gray-100 border-t">
            <a
              href="https://hubflo.com/agents"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center text-gray-400 text-xs hover:underline transition-all"
            >
              <div className="w-4 h-4 rounded-full overflow-hidden mr-1.5 flex-shrink-0">
                <img
                  src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/logo%20squared%20hubflo.webp`}
                  alt="Hubflo Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              Powered by Hubflo
            </a>
          </div>
        )}
      </div>
    </PreviewContainer>
  );
};

export default ChatInterfacePreview;
